import { useRef, useState, useEffect } from "react";
import {
  upload_audio_from_mobile_api,
  upload_multiple_images_api,
} from "../../../DAL/general_routes/general_routes";
import {
  is_file_uploaded,
  markdownToMarkup,
  markupToMarkdown,
  isEmptyMessage,
  handleCardHeight,
  handlePaste,
  isMoreHeight,
  onInputEmojiClick,
} from "../../../utils/classRoomUtils";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { FilePreview } from "./FilePreview";
import { InputActions } from "./InputActions";
import { useContentSetting } from "../../../Hooks/ContentSetting";
import VoiceRecorder from "../VoiceRecorder";
import SpeechRecognition from "../SpeechRecognition";
import ParentMessage from "./ParentMessage";
import GeneralEmojiPicker from "../../GeneralComponents/GeneralEmojiPicker";

export default function InputEditor(props) {
  const {
    messageText,
    setMessageText,
    selectedObject = {},
    setSelectedObject = () => {},
    selectedConversation = {},
    files,
    setFiles,
    isBroadcast,
    editorInit,
    setEditorInit,
    parentMessage,
    setParentMessage = () => {},
  } = props;
  const { t } = useTranslation();

  const [adjustHeight, setAdjustHeight] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStartRecording, setIsStartRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { userInfo, socket } = useContentSetting();
  const [savedRange, setSavedRange] = useState(null);
  const typingTimeout = useRef(null);
  const typingStateRef = useRef({
    conversationId: null,
    active: false,
    stopTimeoutId: null,
  });
  const inputRef = useRef(null);

  const handleUploadImages = async (files) => {
    const uploadedPaths = [];
    const newFiles = [];

    files.forEach((file) => {
      if (is_file_uploaded(file)) {
        uploadedPaths.push(file);
      } else {
        newFiles.push(file);
      }
    });

    if (newFiles.length > 0) {
      const formData = new FormData();
      newFiles.forEach((file, index) => {
        formData.append(`image_${index + 1}`, file);
      });
      const result = await upload_multiple_images_api(formData);
      uploadedPaths.push(...result.image_paths);
    }

    return uploadedPaths;
  };

  const handleSendMessage = async ({ file, mimeType, duration, files }) => {
    let audio_path = "";
    let images_path = [];
    let msgText = messageText;
    if (inputRef.current) {
      msgText = markupToMarkdown(inputRef.current);
    }

    if (!msgText?.trim() && !file && files.length === 0) {
      inputRef.current?.focus();
      enqueueSnackbar(t("chat.typeMessageFirst"), {
        variant: "warning",
      });
      setIsSubmitting(false);
      return;
    }

    if (file && mimeType) {
      const formData = new FormData();
      formData.append("audio", file, `audio.${mimeType}`);
      formData.append("format", mimeType);
      const result = await upload_audio_from_mobile_api(formData);
      audio_path = result.audio_path;
    }

    if (files) {
      images_path = await handleUploadImages(files);
    }

    const socketData = {
      conversation_id: selectedConversation?._id,
      sender_id: userInfo._id,
      message: msgText,
      user_type: userInfo.user_type,
      ...(mimeType && {
        recording_url: audio_path,
        duration: duration * 1000,
      }),
      ...(images_path?.length > 0 && { images: images_path }),
      ...(selectedObject?._id && { message_id: selectedObject?._id }),
      ...(isBroadcast && { is_broadcast: true }),
      ...(parentMessage && { parent_message: parentMessage._id }),
    };

    const finalEvent = selectedObject?._id
      ? "update_message_from_class"
      : "send_message_from_class";

    socket.emit(finalEvent, socketData);
    handleRemoveValues();
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);
    handleSendMessage({ files });
  };

  const handleChangeText = () => {
    if (typingTimeout.current) return;
    typingTimeout.current = true;

    const socketData = {
      conversation: selectedConversation,
      userId: userInfo._id,
      action: "start_typing",
    };

    socket.emit("typing_indicator", socketData);
    const stopTimeoutId = setTimeout(() => {
      socket.emit("typing_indicator", { ...socketData, action: "stop_typing" });
      typingTimeout.current = null;
      typingStateRef.current.active = false;
      typingStateRef.current.stopTimeoutId = null;
    }, 3000);
    typingStateRef.current = {
      conversationId: selectedConversation?._id || null,
      active: true,
      stopTimeoutId,
    };
  };

  const handleChange = () => {
    setEditorInit(true);
    handleChangeText();
    const content = inputRef.current?.innerHtml || inputRef.current?.innerText;
    setMessageText(content);
  };

  const handleRemoveValues = () => {
    setFiles([]);
    setMessageText("");
    setIsSubmitting(false);
    setIsStartRecording(false);
    setSelectedObject?.({});
    setEditorInit(false);
    setIsSpeaking(false);
    setParentMessage(null);
    if (inputRef.current) {
      inputRef.current.innerHTML = "";
    }
  };

  const onCancelUpdate = (e) => {
    e.preventDefault();
    handleRemoveValues();
  };

  const handleStartRecording = () => {
    setIsStartRecording(true);
  };

  useEffect(() => {
    handleCardHeight(adjustHeight, setAdjustHeight);
  }, [adjustHeight]);

  useEffect(() => {
    const editor = inputRef.current;
    if (editorInit || !messageText || !editor) return;
    editor.innerHTML = `${markdownToMarkup(messageText)} `;
    setEditorInit(true);
  }, [messageText, editorInit, inputRef, setEditorInit]);

  useEffect(() => {
    if (isEmptyMessage(messageText) && files.length === 0) {
      setSelectedObject?.({});
      setEditorInit(false);
    }
    setAdjustHeight(true);
  }, [files, messageText, setSelectedObject, setEditorInit, parentMessage]);

  useEffect(() => {
    handleRemoveValues();
  }, [selectedConversation?._id]);

  useEffect(() => {
    return () => {
      const state = typingStateRef.current;
      if (state.stopTimeoutId) {
        clearTimeout(state.stopTimeoutId);
      }
      if (state.active && state.conversationId) {
        socket.emit("typing_indicator", {
          conversation: { _id: state.conversationId },
          userId: userInfo._id,
          action: "stop_typing",
        });
      }
      typingTimeout.current = null;
      typingStateRef.current = {
        conversationId: null,
        active: false,
        stopTimeoutId: null,
      };
    };
  }, [socket, userInfo._id]);

  const recordingData = {
    setIsSubmitting,
    handleSubmit: handleSendMessage,
    isSubmitting,
    setIsStartRecording,
  };

  const speakData = {
    handleSubmit,
    isSubmitting,
    setIsSubmitting,
    setMessageText,
    setIsSpeaking,
    messageText,
  };

  const restoreSelection = () => {
    if (savedRange) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedRange);
    }
  };

  const onEmojiClick = (emojiObject) => {
    restoreSelection();
    onInputEmojiClick({ emojiObject, inputRef, handleChange });
  };

  const handleBlur = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      setSavedRange(range);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handlePasteWithImages = (e) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) {
      handlePaste(e);
      return;
    }
    const imageFiles = [];
    if (clipboardData.items) {
      for (const item of clipboardData.items) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }
    }
    if (imageFiles.length === 0 && clipboardData.files) {
      imageFiles.push(
        ...Array.from(clipboardData.files).filter((f) =>
          f.type.startsWith("image/")
        )
      );
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      setFiles((prev) => [...prev, ...imageFiles]);
    } else {
      handlePaste(e);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    const droppedFiles = e.dataTransfer?.files;
    if (!droppedFiles?.length) return;
    const imageFiles = Array.from(droppedFiles).filter((f) =>
      f.type.startsWith("image/")
    );
    if (imageFiles.length > 0) {
      setFiles((prev) => [...prev, ...imageFiles]);
    }
  };

  return (
    <form
      className="w-100 write-message"
      onSubmit={handleSubmit}
      id="write-message-form"
    >
      <ParentMessage {...props} />
      {isStartRecording ? (
        <VoiceRecorder {...recordingData} />
      ) : isSpeaking ? (
        <SpeechRecognition {...speakData} />
      ) : (
        <div className="input-editor-box">
          <div className="content-editable-box">
            <div className={`message-input-box`}>
              <div
                className={`text-input-box ${isMoreHeight(messageText, files)} ${isDraggingOver ? "drag-over" : ""}`}
                id="text-input-box"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="contenteditable-box">
                  <div
                    contentEditable={true}
                    ref={inputRef}
                    className={`contenteditable ${
                      isEmptyMessage(messageText) ? "empty" : ""
                    }`}
                    data-placeholder={t("chat.messagePlaceholder")}
                    onInput={handleChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePasteWithImages}
                    onBlur={handleBlur}
                  />
                </div>
                <div className="d-flex justify-content-between align-items-end">
                  <div className="image-preview-box-width mt-1">
                    <FilePreview {...{ files, setFiles }} />
                  </div>
                  <InputActions
                    {...{
                      files,
                      setFiles,
                      selectedObject,
                      onCancelUpdate,
                      isSubmitting,
                      handleSubmit,
                      handleStartRecording,
                      setIsSpeaking,
                      setShowEmojiPicker,
                    }}
                    isEmpty={isEmptyMessage(messageText)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showEmojiPicker && (
        <GeneralEmojiPicker {...{ onEmojiClick, setShowEmojiPicker }} />
      )}
    </form>
  );
}
